import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import {
  isMobile,
  isTablet,
  isIos,
  HealthMonitorCodes,
  SessionState,
} from '@biosensesignal/web-sdk';
import { useMediaPredicate } from 'react-media-hook';
import {
  useError,
  useLicenseKey,
  useProductId,
  useMeasurementDuration,
  useMonitor,
  usePageVisibility,
  usePrevious,
  useTimer,
  useWarning,
} from '../hooks';
import Stats from './Stats';
import StartButton from './StartButton';
import { mirror } from '../style/mirror';
import { Flex } from './shared/Flex';
import Timer from './Timer';
import media from '../style/media';
import InfoBar from './InfoBar';
import { ErrorAlert, InfoAlert, WarningAlert } from './alert';
import Loader from './Loader';
import { VideoReadyState } from '../types';
import TopBar from './TopBar';
import ResultsModal from './ResultsModal';
import Mask from '../assets/mask.svg';

const MonitorWrapper = styled(Flex)<{ isSettingsOpen: boolean }>`
  flex-direction: column;
  width: 100%;
  justify-content: start;
  align-items: center;
  flex: 1;
  z-index: ${({ isSettingsOpen }) => isSettingsOpen && '-1'};
  ${media.mobile`
    padding-bottom: 120px;
  `}
  ${media.tablet`
    width: fit-content;
    justify-content: center;
    padding-bottom: 0;
  `}
`;

const MeasurementContentWrapper = styled(Flex)<{ isMobile: boolean }>`
  width: auto;
  height: ${({ isMobile }) => isMobile ? '100vh' : 'auto'};
  flex-direction: column;
  justify-content: ${({ isMobile }) => isMobile ? 'flex-start' : 'flex-start'};
  align-items: center;
  position: ${({ isMobile }) => isMobile ? 'relative' : 'static'};
  ${media.mobile`
    margin: 0;
    padding: 10px;
    overflow: hidden;
  `}
`;

const VideoAndStatsWrapper = styled(Flex)<{ isMobile: boolean }>`
  position: relative;
  justify-content: center;
  width: 100%;
  height: ${({ isMobile }) => isMobile ? 'auto' : '100%'};
  flex: ${({ isMobile }) => isMobile ? '0 0 auto' : '1'};
  ${media.mobile`
    max-height: calc(100vh - 200px);
    min-height: 250px;
  `}
  ${media.tablet`
    width: 812px;
    height: 609px;
  `} ${media.wide`
    width: 1016px;
    height: 762px;
  `};
`;

const VideoWrapper = styled.div`
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const Img = styled.img<{ isDesktop: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 1;
  object-fit: ${({ isDesktop }) => (isDesktop ? 'contain' : 'cover')};
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  ${mirror};
`;

const ButtonWrapper = styled(Flex)`
  flex: 0 0 auto;
  z-index: 3;
  width: 100%;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 20px;
  ${media.mobile`
    position: fixed;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    width: auto;
    margin: 0;
    z-index: 1000;
  `}
  ${media.tablet`
    padding: 0;
    height: auto;
    width: auto;
    position: absolute;
    right: 0;
    bottom: 42%;
    margin-right: 60px;
    margin-top: 0;
    transform: none;
  `}
`;

const ButtomTimerWrapper = styled(Flex)`
  display: none;
  ${media.tablet`
    justify-content: center;
    align-items: center;
    margin-top: 10px;
    height: 30px;
    display: flex;
  `}
`;

const InfoBarWrapper = styled.div`
  height: 25px;
  width: 100%;
  display: flex;
  align-items: flex-end;
  ${media.mobile`
    flex: 0.45;
  `}
`;

const BiosenseSignalMonitor = ({
  showMonitor,
  cameraId,
  onLicenseStatus,
  onSettingsClick,
  isSettingsOpen,
}) => {
  if (!showMonitor) {
    return null;
  }

  const video = useRef<HTMLVideoElement>(null);
  const [isMeasurementEnabled, setIsMeasurementEnabled] = useState<boolean>(
    false,
  );
  const [startMeasuring, setStartMeasuring] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingTimeoutPromise, setLoadingTimeoutPromise] = useState<number>();
  const [showResultsModal, setShowResultsModal] = useState<boolean>(false);
  const [lastMeasurementResults, setLastMeasurementResults] = useState<any>(null);
  const [hasShownModalForThisMeasurement, setHasShownModalForThisMeasurement] = useState<boolean>(false);
  const isPageVisible = usePageVisibility();
  const isMediaTablet = useMediaPredicate('(min-width: 1000px)');
  const [processingTime] = useMeasurementDuration();
  const [licenseKey] = useLicenseKey();
  const [productId] = useProductId();
  const {
    sessionState,
    vitalSigns,
    offlineMeasurements,
    error,
    warning,
    info,
  } = useMonitor(
    video,
    cameraId,
    processingTime,
    licenseKey,
    productId,
    startMeasuring,
  );
  const prevSessionState = usePrevious(sessionState);
  const errorMessage = useError(error);
  const warningMessage = useWarning(warning);

  const handleCloseResultsModal = useCallback(() => {
    console.log('User closed results modal');
    setShowResultsModal(false);
  }, []);

  const isMeasuring = useCallback(
    () => sessionState === SessionState.MEASURING,
    [sessionState],
  );

  const timerSeconds = useTimer(isMeasuring(), processingTime);

  const isVideoReady = useCallback(
    () => video.current?.readyState === VideoReadyState.HAVE_ENOUGH_DATA,
    [],
  );

  const handleButtonClick = useCallback(() => {
    setIsLoading(true);
    if (sessionState === SessionState.ACTIVE) {
      setStartMeasuring(true);
      setLoadingTimeoutPromise(
        window.setTimeout(() => setIsLoading(true), processingTime * 1000),
      );
    } else if (isMeasuring()) {
      clearTimeout(loadingTimeoutPromise);
      setStartMeasuring(false);
    }
  }, [sessionState, setIsLoading, processingTime]);

  useEffect(() => {
    if (isMeasuring()) {
      setIsLoading(false);
      if (errorMessage) {
        setIsMeasurementEnabled(false);
      } else {
        setIsMeasurementEnabled(true);
      }
      !isPageVisible && setStartMeasuring(false);
    } else if (
      (sessionState === SessionState.ACTIVE ||
        sessionState === SessionState.TERMINATED) &&
      errorMessage
    ) {
      setIsMeasurementEnabled(false);
    }
    if (
      sessionState === SessionState.ACTIVE &&
      prevSessionState !== sessionState
    ) {
      setStartMeasuring(false);
      setIsLoading(false);
    }
  }, [errorMessage, sessionState, isPageVisible]);

  useEffect(() => {
    onLicenseStatus(!(error?.code in HealthMonitorCodes));
  }, [error]);

  // Simple timer-based trigger: Show modal when timer completes and we have data
  useEffect(() => {
    console.log('Timer check:', {
      timerSeconds,
      processingTime,
      timerCompleted: timerSeconds >= processingTime,
      hasVitalSigns: !!vitalSigns,
      hasError: !!errorMessage,
      modalOpen: showResultsModal,
      alreadyShownForThisMeasurement: hasShownModalForThisMeasurement,
    });
    
    // Show modal when timer reaches the processing time and we have vital signs
    if (
      timerSeconds >= processingTime && // Timer completed
      vitalSigns && // We have vital signs data
      !errorMessage && // No errors
      !showResultsModal && // Modal not already showing
      !hasShownModalForThisMeasurement // Haven't shown modal for this measurement yet
    ) {
      console.log('✅ Timer completed! Showing results modal with vital signs after 1 second delay');
      // Add 1 second delay to allow measurements to fully populate
      const delayTimeout = setTimeout(() => {
        setLastMeasurementResults(vitalSigns);
        setShowResultsModal(true);
        setHasShownModalForThisMeasurement(true); // Mark as shown for this measurement
      }, 1000);
      
      // Cleanup timeout if component unmounts or dependencies change
      return () => clearTimeout(delayTimeout);
    }
  }, [timerSeconds, processingTime, vitalSigns, errorMessage, showResultsModal, hasShownModalForThisMeasurement]);

  // Reset the flag when starting a new measurement
  useEffect(() => {
    if (isMeasuring()) {
      setHasShownModalForThisMeasurement(false);
    }
  }, [isMeasuring]);

  const mobile = useMemo(() => isMobile(), []);
  const desktop = useMemo(() => !isTablet() && !isMobile(), []);

  return (
    <>
      <TopBar onSettingsClick={onSettingsClick} isMeasuring={isMeasuring()} />
      <MonitorWrapper isSettingsOpen={isSettingsOpen}>
        <MeasurementContentWrapper isMobile={mobile}>
          <InfoBarWrapper>
            <InfoBar
              showTimer={isMeasurementEnabled && !isMediaTablet}
              isMeasuring={isMeasuring()}
              durationSeconds={processingTime}
              offlineMeasurements={offlineMeasurements}
            />
          </InfoBarWrapper>
          <VideoAndStatsWrapper isMobile={mobile}>
            <VideoWrapper>
              <Img src={Mask} isDesktop={desktop} />
              <Video ref={video} id="video" muted={true} playsInline={true} />
            </VideoWrapper>
            {(isMeasuring()
              ? !errorMessage && !warningMessage
              : !errorMessage) &&
              isMeasurementEnabled && (
                <Stats 
                  vitalSigns={{
                    pulseRate: vitalSigns.pulseRate,
                    respirationRate: vitalSigns.respirationRate,
                    stress: vitalSigns.stressLevel,
                    hrvSdnn: vitalSigns.sdnn,
                    spo2: vitalSigns.oxygenSaturation,
                    bloodPressure: vitalSigns.bloodPressure,
                  }} 
                />
              )}
            <ErrorAlert message={errorMessage} />
            {isMeasuring() && <WarningAlert message={warningMessage} />}
            {isMeasuring() && <InfoAlert message={info.message} />}
            {!isVideoReady() && licenseKey && <Loader />}
          </VideoAndStatsWrapper>
          <ButtomTimerWrapper>
            {isMeasurementEnabled && (
              <Timer started={isMeasuring()} durationSeconds={processingTime} />
            )}
          </ButtomTimerWrapper>
          <ButtonWrapper>
            <StartButton
              isLoading={isLoading}
              isMeasuring={isMeasuring()}
              onClick={handleButtonClick}
            />

          </ButtonWrapper>
        </MeasurementContentWrapper>
      </MonitorWrapper>
      {lastMeasurementResults && (
        <ResultsModal
          isOpen={showResultsModal}
          onClose={handleCloseResultsModal}
          vitalSigns={lastMeasurementResults}
        />
      )}
    </>
  );
};

export default BiosenseSignalMonitor;


